import './styles.scss'
import { useEffect, useRef, useState } from 'react'
import { Color } from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import TextStyle from '@tiptap/extension-text-style'
import { EditorProvider, useCurrentEditor } from '@tiptap/react'
import Image from '@tiptap/extension-image'
import StarterKit from '@tiptap/starter-kit'
import React from 'react'
import LinearProgress from '@mui/material/LinearProgress'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import Select from '@mui/material/Select'
import Box from '@mui/material/Box'
import CloseIcon from '@mui/icons-material/Close'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import ImageIcon from '@mui/icons-material/Image'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough'
import CodeIcon from '@mui/icons-material/Code'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import DataObjectIcon from '@mui/icons-material/DataObject'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import PaletteIcon from '@mui/icons-material/Palette'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import FormatClearIcon from '@mui/icons-material/FormatClear'
import LayersClearIcon from '@mui/icons-material/LayersClear'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import { storeFile, storeVideoForTranscoding } from '../../../firebase/CloudStorage'
import { useSelector } from 'react-redux'
import { Video } from './VideoExtension'

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatSpeed = (bps) => {
  if (!Number.isFinite(bps) || bps <= 0) return '—';
  return `${formatBytes(bps)}/s`;
};

const formatEta = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return '—';
  if (sec < 1) return 'less than a second';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

const truncate = (str, max = 38) =>
  !str ? '' : str.length <= max ? str : `${str.slice(0, max - 1)}…`;

const UploadProgressCard = ({ upload, onCancel }) => {
  if (!upload) return null;

  const { kind, filename, totalBytes, bytesTransferred, percent, speedBytesPerSec, etaSeconds, status, errorMessage } = upload;

  const isDone = status === 'done';
  const isError = status === 'error';
  const isFinalizing = status === 'finalizing';

  const TypeIcon = kind === 'video' ? VideoFileIcon : ImageIcon;

  let statusLine;
  if (isError) {
    statusLine = errorMessage || 'Upload failed';
  } else if (isDone) {
    statusLine = 'Upload complete';
  } else if (isFinalizing) {
    statusLine = 'Finalizing on server…';
  } else {
    statusLine = `${formatBytes(bytesTransferred)} of ${formatBytes(totalBytes)} • ${formatSpeed(speedBytesPerSec)} • ${formatEta(etaSeconds)} left`;
  }

  return (
    <div className={`upload-progress-card ${isError ? 'is-error' : ''} ${isDone ? 'is-done' : ''}`}>
      <div className="upload-progress-card__icon">
        {isError ? (
          <ErrorOutlineIcon fontSize="medium" />
        ) : isDone ? (
          <CheckCircleIcon fontSize="medium" />
        ) : (
          <TypeIcon fontSize="medium" />
        )}
      </div>

      <div className="upload-progress-card__body">
        <div className="upload-progress-card__header">
          <span className="upload-progress-card__filename" title={filename}>
            {truncate(filename)}
          </span>
          <span className="upload-progress-card__percent">
            {isError ? '!' : isDone ? '100%' : `${Math.floor(percent)}%`}
          </span>
        </div>

        <div className="upload-progress-card__bar-wrap">
          <LinearProgress
            variant={isFinalizing ? 'indeterminate' : 'determinate'}
            value={Math.min(100, percent || 0)}
            className="upload-progress-card__bar"
            color={isError ? 'error' : isDone ? 'success' : 'primary'}
          />
        </div>

        <div className="upload-progress-card__meta">{statusLine}</div>
      </div>

      {!isDone && (
        <IconButton
          aria-label={isError ? 'Dismiss' : 'Cancel upload'}
          size="small"
          className="upload-progress-card__cancel"
          onClick={onCancel}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

/* ---------- Toolbar building blocks ---------- */

const ToolDivider = () => <span className="rte-toolbar__divider" aria-hidden="true" />;

const ToolButton = ({ label, shortcut, icon, onClick, isActive, disabled }) => (
  <Tooltip
    title={
      <span>
        {label}
        {shortcut && <span className="rte-tooltip-shortcut">{shortcut}</span>}
      </span>
    }
    arrow
    placement="top"
    disableInteractive
  >
    <span className="rte-toolbar__btn-wrap">
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        className={`rte-toolbar__btn ${isActive ? 'is-active' : ''}`}
        aria-label={label}
        aria-pressed={!!isActive}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

const HEADING_OPTIONS = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'h4', label: 'Heading 4' },
  { value: 'h5', label: 'Heading 5' },
  { value: 'h6', label: 'Heading 6' },
];

const HeadingSelect = ({ editor }) => {
  let current = 'paragraph';
  for (let level = 1; level <= 6; level++) {
    if (editor.isActive('heading', { level })) {
      current = `h${level}`;
      break;
    }
  }

  const onChange = (e) => {
    const v = e.target.value;
    if (v === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else {
      const level = parseInt(v.slice(1), 10);
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  return (
    <Select
      size="small"
      value={current}
      onChange={onChange}
      className="rte-toolbar__heading-select"
      MenuProps={{ PaperProps: { className: 'rte-toolbar__menu-paper' } }}
      renderValue={(val) => HEADING_OPTIONS.find((o) => o.value === val)?.label}
    >
      {HEADING_OPTIONS.map((opt) => {
        const className = opt.value === 'paragraph'
          ? 'rte-heading-option rte-heading-option--p'
          : `rte-heading-option rte-heading-option--${opt.value}`;
        return (
          <MenuItem key={opt.value} value={opt.value} className={className}>
            {opt.label}
          </MenuItem>
        );
      })}
    </Select>
  );
};

const COLOR_SWATCHES = [
  { value: '#1f2540', label: 'Default' },
  { value: '#4f5bd5', label: 'Indigo' },
  { value: '#958DF1', label: 'Purple' },
  { value: '#0ea5e9', label: 'Sky' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#ec4899', label: 'Pink' },
];

const ColorPicker = ({ editor }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const activeColor = editor.getAttributes('textStyle').color || null;

  return (
    <>
      <ToolButton
        label="Text color"
        icon={
          <span className="rte-color-trigger">
            <PaletteIcon fontSize="small" />
            <span
              className="rte-color-trigger__swatch"
              style={{ background: activeColor || '#9ca3af' }}
            />
          </span>
        }
        isActive={!!activeColor}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ className: 'rte-color-popover' }}
      >
        <div className="rte-color-popover__title">Text color</div>
        <div className="rte-color-popover__swatches">
          {COLOR_SWATCHES.map((c) => {
            const isActive = activeColor && activeColor.toLowerCase() === c.value.toLowerCase();
            return (
              <button
                key={c.value}
                title={c.label}
                aria-label={c.label}
                className={`rte-color-swatch ${isActive ? 'is-active' : ''}`}
                style={{ background: c.value }}
                onClick={() => {
                  editor.chain().focus().setColor(c.value).run();
                  setAnchorEl(null);
                }}
              />
            );
          })}
        </div>
        <button
          className="rte-color-popover__reset"
          onClick={() => {
            editor.chain().focus().unsetColor().run();
            setAnchorEl(null);
          }}
        >
          <FormatClearIcon fontSize="small" />
          Reset color
        </button>
      </Popover>
    </>
  );
};

const OverflowMenu = ({ editor }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  return (
    <>
      <ToolButton
        label="More"
        icon={<MoreHorizIcon fontSize="small" />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        PaperProps={{ className: 'rte-toolbar__menu-paper' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            editor.chain().focus().unsetAllMarks().run();
            close();
          }}
        >
          <ListItemIcon><FormatClearIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Clear formatting</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().clearNodes().run();
            close();
          }}
        >
          <ListItemIcon><LayersClearIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Clear blocks</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            editor.chain().focus().setHardBreak().run();
            close();
          }}
        >
          <ListItemIcon><KeyboardReturnIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Insert line break</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

/* ---------- MenuBar ---------- */

const MenuBar = ({ uploads, setUploads, uploadTasksRef }) => {
  const { editor } = useCurrentEditor();
  const loggedInUserAccount = useSelector(state=>state.loggedInUserAccount);
  const mediaInputRef = useRef();

  if (!editor) {
    return null
  }

  const startUpload = (kind, file) => {
    if (!file) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // The transcodeId doubles as the Storage object name and the
    // `videoTranscodes/{id}` Firestore doc id, so it must be stable and
    // unique. We deliberately don't reuse `id` here — `id` is a UI-only
    // upload identifier, while the transcodeId travels into the document.
    const filenameInStorage = `${loggedInUserAccount.id}_Event${kind === 'video' ? 'Video' : 'Image'}_${id}`;

    setUploads((prev) => [
      ...prev,
      {
        id,
        kind,
        filename: file.name,
        totalBytes: file.size,
        bytesTransferred: 0,
        percent: 0,
        speedBytesPerSec: 0,
        etaSeconds: Infinity,
        status: 'uploading',
        errorMessage: null,
      },
    ]);

    const updateUpload = (patch) => {
      setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
    };

    const dropAfter = (ms) => {
      setTimeout(() => {
        setUploads((prev) => prev.filter((u) => u.id !== id));
        delete uploadTasksRef.current[id];
      }, ms);
    };

    const onProgress = (progress) => {
      const reachedFull = progress.bytesTransferred >= progress.totalBytes && progress.totalBytes > 0;
      updateUpload({
        bytesTransferred: progress.bytesTransferred,
        totalBytes: progress.totalBytes,
        percent: progress.percent,
        speedBytesPerSec: progress.speedBytesPerSec,
        etaSeconds: progress.etaSeconds,
        status: reachedFull ? 'finalizing' : 'uploading',
      });
    };

    const onError = (err) => {
      updateUpload({ status: 'error', errorMessage: err && err.message ? err.message : 'Upload failed' });
      dropAfter(6000);
    };

    let task;
    if (kind === 'video') {
      // Videos go through the transcode pipeline: upload to videos/raw/{id},
      // a Cloud Function transcodes to H.264 + faststart MP4, and the editor
      // node view subscribes to videoTranscodes/{id} to swap the URL in once
      // it's ready. The progress card here represents only the upload leg —
      // the in-document placeholder represents the transcode leg.
      task = storeVideoForTranscoding(
        filenameInStorage,
        file,
        ({ transcodeId }) => {
          updateUpload({ status: 'done', percent: 100, bytesTransferred: file.size });
          // Video is a selectable block atom. If we insert ONLY the video,
          // ProseMirror's default cursor placement after the insert lands as
          // a NodeSelection on the atom (because it's selectable and there
          // may be no inline-content node after it to land in). The next
          // async upload's insertContent then *replaces* that selection —
          // silently overwriting the just-inserted video.
          //
          // Inserting [video, paragraph] together guarantees an inline-
          // content node immediately after the atom. selectionToInsertionEnd
          // lands at the end of the inserted paragraph, which is a real
          // TextSelection. Subsequent uploads append into that paragraph
          // instead of replacing the video.
          editor
            .chain()
            .focus()
            .insertContent([
              {
                type: 'video',
                attrs: { src: null, transcodeId },
              },
              { type: 'paragraph' },
            ])
            .run();
          dropAfter(1500);
        },
        onProgress,
        onError
      );
    } else {
      task = storeFile(
        filenameInStorage,
        file,
        (url) => {
          updateUpload({ status: 'done', percent: 100, bytesTransferred: file.size });
          editor.chain().focus().setImage({ src: url }).run();
          dropAfter(1500);
        },
        onProgress,
        onError
      );
    }

    uploadTasksRef.current[id] = task;
  };

  const onSelectMedia = (event) => {
    const files = Array.from(event.currentTarget.files || []);
    event.currentTarget.value = '';
    files.forEach((file) => {
      const kind = file.type.startsWith('video/') ? 'video' : 'image';
      startUpload(kind, file);
    });
  };

  const cancelUpload = (id) => {
    const task = uploadTasksRef.current[id];
    try {
      if (task && typeof task.cancel === 'function') task.cancel();
    } catch (e) { /* noop */ }
    setUploads((prev) => prev.filter((u) => u.id !== id));
    delete uploadTasksRef.current[id];
  };

  const can = (cmd) => {
    try {
      return editor.can().chain().focus()[cmd]().run();
    } catch (e) {
      return true;
    }
  };

  return (
    <>
      <Box className="rte-toolbar" role="toolbar" aria-label="Description formatting">
        <div className="rte-toolbar__group">
          <HeadingSelect editor={editor} />
        </div>

        <ToolDivider />

        <div className="rte-toolbar__group">
          <ToolButton
            label="Bold" shortcut="⌘B"
            icon={<FormatBoldIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!can('toggleBold')}
            isActive={editor.isActive('bold')}
          />
          <ToolButton
            label="Italic" shortcut="⌘I"
            icon={<FormatItalicIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!can('toggleItalic')}
            isActive={editor.isActive('italic')}
          />
          <ToolButton
            label="Strikethrough" shortcut="⌘⇧X"
            icon={<FormatStrikethroughIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!can('toggleStrike')}
            isActive={editor.isActive('strike')}
          />
          <ToolButton
            label="Inline code" shortcut="⌘E"
            icon={<CodeIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleCode().run()}
            disabled={!can('toggleCode')}
            isActive={editor.isActive('code')}
          />
          <ColorPicker editor={editor} />
        </div>

        <ToolDivider />

        <div className="rte-toolbar__group">
          <ToolButton
            label="Bullet list"
            icon={<FormatListBulletedIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
          />
          <ToolButton
            label="Numbered list"
            icon={<FormatListNumberedIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
          />
        </div>

        <ToolDivider />

        <div className="rte-toolbar__group">
          <ToolButton
            label="Quote"
            icon={<FormatQuoteIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
          />
          <ToolButton
            label="Code block"
            icon={<DataObjectIcon fontSize="small" />}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
          />
          <ToolButton
            label="Divider"
            icon={<HorizontalRuleIcon fontSize="small" />}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          />
        </div>

        <ToolDivider />

        <div className="rte-toolbar__group rte-toolbar__group--media">
          <ToolButton
            label="Insert photo or video"
            icon={<PermMediaIcon fontSize="small" />}
            onClick={() => mediaInputRef.current.click()}
          />
        </div>

        <ToolDivider />

        <div className="rte-toolbar__group">
          <ToolButton
            label="Undo" shortcut="⌘Z"
            icon={<UndoIcon fontSize="small" />}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!can('undo')}
          />
          <ToolButton
            label="Redo" shortcut="⌘⇧Z"
            icon={<RedoIcon fontSize="small" />}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!can('redo')}
          />
        </div>

        <div className="rte-toolbar__spacer" />

        <div className="rte-toolbar__group">
          <OverflowMenu editor={editor} />
        </div>

        <input onChange={onSelectMedia} multiple ref={mediaInputRef} type='file' accept='image/*,video/*' hidden/>
      </Box>

      {uploads.length > 0 && (
        <div className="upload-progress-stack">
          {uploads.map((u) => (
            <UploadProgressCard key={u.id} upload={u} onCancel={() => cancelUpload(u.id)} />
          ))}
        </div>
      )}
    </>
  )
}

const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure({ types: [ListItem.name] }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
  }),
  Image.configure({
    inline: true,
  }),
  Video
]

export const RichTextEditor = (props) => {
  const { onUploadStateChange } = props;
  const [uploads, setUploads] = useState([]);
  const uploadTasksRef = useRef({});
  const hasActiveUploads = uploads.some((upload) => upload.status === 'uploading' || upload.status === 'finalizing');

  useEffect(() => {
    if(onUploadStateChange) onUploadStateChange(hasActiveUploads);
  }, [hasActiveUploads, onUploadStateChange]);

  if(props.readonly){
    return (
      <EditorProvider
        editable={false}
        extensions={extensions}
        content={props.contentHTML}
      />
    );
  }

  return (
    <div className='edit'>
      <EditorProvider
        slotBefore={<MenuBar uploads={uploads} setUploads={setUploads} uploadTasksRef={uploadTasksRef} />}
        extensions={extensions}
        content={props.contentHTML}
        onUpdate={editor=>{
            props.setContentHTML(editor.editor.getHTML());
            props.setContentJSON(editor.editor.getJSON());
        }}
      />
    </div>
  )
}
